/**
 * assetKindColors — functional (category-coding) identity for the content
 * browser's asset kinds: the color, and the short label the card badge shows.
 * Unreal convention, same as waypointColors.js and the node/pin/wire colors:
 * the color IS the category, so it deliberately
 * does NOT follow the palette theme — a body animation has to read as a body
 * animation in every palette. Themeable surfaces around them (panel
 * backgrounds, dim text, success/warning/error tones) use var(--m-*) tokens.
 * Allowlisted as functional in tests/unit/paletteEnforcement.test.mjs.
 *
 * This map existed three times — AssetIcon's COLOR, AssetCard's KIND_COLOR and
 * Inspector's KIND_COLOR — with the same values and different subsets, so a new
 * kind had to be added in three places and a card could disagree with the
 * inspector beside it. One map, one place to extend.
 *
 * The palette offers no set of mutually-distinct hues to map nine asset kinds
 * onto, and collapsing them onto the wire colors would make body/face or
 * material/prefab read the same — which is why these are literals rather than
 * tokens.
 */
import { SURFACE_LABELS, SURFACE_GLYPH } from './outlinerTree.js'

export const ASSET_KIND_COLORS = {
  body_anim:       '#5070d0',
  face_anim:       '#d07050',
  audio:           '#00c4ad',
  prefab:          '#c08cff',
  rig:             '#facc15',
  texture:         '#9ec45c',
  material:        '#8b6cff',
  environment:     '#5cd4ff',
  ogs:             '#5cd4ff',
  garment:         '#f2789f',
  blueprint_asset: '#4ade80',
}

/** Neutral for an unknown kind. */
export const ASSET_KIND_FALLBACK = '#6e7079'

/** The identity color for a kind, or the neutral fallback. */
export function assetKindColor(kind) {
  return ASSET_KIND_COLORS[kind] ?? ASSET_KIND_FALLBACK
}

// The card badge derives its label from the kind by replacing underscores, which
// reads correctly for every kind whose internal name IS its display name. Where
// it does not, the label is stated here: `blueprint_asset` is called that in code
// only to keep it distinct from the singleton "blueprint" document, and no user
// should have to see the distinction as "BLUEPRINT ASSET".
const ASSET_KIND_SHORT_LABEL = {
  blueprint_asset: 'BLUEPRINT',
}

/** Short, uppercase label for a kind — what the card's kind dot shows. */
export function assetKindShortLabel(kind) {
  if (!kind) return ''
  return ASSET_KIND_SHORT_LABEL[kind] ?? kind.replace(/_/g, ' ').toUpperCase()
}

// A blueprint row IS one graph, so its card reads as that graph rather than as a
// generic "blueprint". Labels and glyphs come from outlinerTree.js — the same
// three the outliner rows, the tool rail and the browser tab title already use,
// so one graph reads the same everywhere it appears.
export function blueprintGraphLabel(graphKind) {
  return SURFACE_LABELS[graphKind] ?? 'Blueprint'
}

/** Uppercase card-dot label for one graph kind, e.g. 'BODY GRAPH'. */
export function blueprintGraphShortLabel(graphKind) {
  const label = SURFACE_LABELS[graphKind]
  return label ? label.toUpperCase() : 'BLUEPRINT'
}

/** The outliner's glyph for one graph kind. */
export function blueprintGraphGlyph(graphKind) {
  return SURFACE_GLYPH[graphKind] ?? null
}

/**
 * Open one graph's editor surface, reporting whether it could.
 *
 * `editors.open` is registered BY BlueprintEditor (BlueprintEditor.jsx), so with
 * the editor unmounted menubarCommands.run() warns and returns undefined — the
 * click would appear to do nothing. This repo's rule is that an unavailable
 * action says why (see voiceInputState.js's header for the bug that taught it),
 * so the caller gets a boolean and surfaces the reason itself.
 *
 * Lives here beside the other graph-identity helpers rather than in a component,
 * so the three call sites (card double-click, context menu, inspector) share one
 * answer about whether the surface can be shown.
 */
export function openGraphSurface(graphKind, menubar) {
  if (!graphKind) return false
  const available = typeof menubar?.get?.().commands?.['editors.open'] === 'function'
  if (!available) return false
  menubar.run('editors.open', graphKind)
  return true
}
